import { useMemo, useState, type CSSProperties, type PointerEvent } from 'react';
import { endpoints } from '../api';
import { EmptyState, ErrorState, LoadingState, Section, number } from '../components';
import { useApiQuery } from '../hooks';
import { OTHER_MATERIALS, miningDailyVolume } from '../mining-analysis';
import type { MiningLedger } from '../mining-types';
import { axisMoney, clamp, dayLabel, keyboardIndex, makeDateTicks, makeTicks, shortDayLabel, useWidth, valueDomain } from './business-charts';

// Validated for dark surfaces and red-green colour blindness; the tooltip and legend name every material.
const COLORS = ['#2a8fd0', '#d4702a', '#8f7ae0', '#a88a10', '#0f9488'];
const OTHER_COLOR = '#6b665f';
const HEIGHT = 240;
const LEFT = 62;
const RIGHT = 8;
const TOP = 10;
const BOTTOM = 24;
const GAP = 2;

const utcDay = (date: string) => new Date(`${date}T00:00:00Z`);

export function MiningDailyVolume() {
  const query = useApiQuery<MiningLedger>(endpoints.miningLedger);
  const series = useMemo(() => query.data && miningDailyVolume(query.data.entries, new Date().toISOString().slice(0, 10)), [query.data]);
  const [selected, setSelected] = useState<number | null>(null);
  const [ref, width] = useWidth();
  const color = (id: number) => id === OTHER_MATERIALS ? OTHER_COLOR : COLORS[series?.materials.findIndex((item) => item.id === id) ?? 0];

  const action = <a className="link" href="#/mining">Minería</a>;
  const title = 'Minería · m³ por familia de material y día (UTC)';
  if (!series || series.materials.length === 0) return <Section title={title} action={action}>
    {query.loading && !query.data && <LoadingState />}
    {query.error && !query.data && <ErrorState error={query.error} onRetry={() => void query.reload()} />}
    {query.data && <EmptyState title="Sin extracciones en estos 30 días" />}
  </Section>;

  const { days, materials, unknownVolumeCount } = series;
  const [low, high] = valueDomain(days.map((day) => day.total), true);
  const plotWidth = width - LEFT - RIGHT;
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const slot = plotWidth / days.length;
  const barWidth = Math.max(2, slot - GAP);
  const x = (index: number) => LEFT + slot * index + slot / 2;
  const y = (value: number) => TOP + ((high - value) / (high - low)) * plotHeight;
  const active = selected === null ? null : clamp(selected, 0, days.length - 1);
  const select = (event: PointerEvent<SVGRectElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (bounds.width) setSelected(clamp(Math.floor(((event.clientX - bounds.left) / bounds.width) * days.length), 0, days.length - 1));
  };

  return <Section title={title} action={action}>
    <figure className="chart" data-chart="mining-volume">
      <div className="chart-legend" aria-hidden="true">{materials.map(({ id, name }) => <span key={id} className="chart-key" style={{ '--key': color(id) } as CSSProperties}>{name}</span>)}</div>
      <div className="chart-plot" ref={ref}>
        <svg height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role="img" tabIndex={0}
          aria-label={`Metros cúbicos minados al día por familia de material durante ${days.length} días: ${materials.map((item) => item.name).join(', ')}. Usa las flechas para consultar cada día.`}
          onKeyDown={(event) => { const next = keyboardIndex(event, active, days.length, days.length - 1); if (next !== null) setSelected(next); }}
          onFocus={() => setSelected(active ?? days.length - 1)} onBlur={() => setSelected(null)}>
          {makeTicks(low, high, 4).map((tick, index) => <g className="chart-axis" key={index}>
            <line x1={LEFT} x2={width - RIGHT} y1={y(tick)} y2={y(tick)} className={Math.abs(tick) < 1e-9 ? 'chart-zero' : undefined} />
            <text x={LEFT - 8} y={y(tick) + 4} textAnchor="end">{axisMoney(tick)}</text>
          </g>)}
          {makeDateTicks(days.length, width < 420 ? 3 : 6).map((index) => <text key={index} x={x(index)} y={HEIGHT - 7} textAnchor={index === 0 ? 'start' : index === days.length - 1 ? 'end' : 'middle'}>{shortDayLabel.format(utcDay(days[index].date))}</text>)}
          {days.map((day, index) => {
            let base = 0;
            return <g key={day.date}>{day.segments.map((segment) => {
              const top = y(base + segment.volume);
              const bottom = y(base);
              base += segment.volume;
              // The gap keeps stacked materials apart without relying on their hue.
              return <rect key={segment.id} className="chart-bar" fill={color(segment.id)} x={x(index) - barWidth / 2} y={top} width={barWidth} height={Math.max(bottom - top - 1, 1)} />;
            })}</g>;
          })}
          {active !== null && <line className="chart-cursor" x1={x(active)} x2={x(active)} y1={TOP} y2={HEIGHT - BOTTOM} />}
          <rect className="chart-hit" x={LEFT} y={TOP} width={Math.max(plotWidth, 0)} height={plotHeight} onPointerMove={select} onPointerDown={select} onPointerLeave={() => setSelected(null)} />
        </svg>
        {active !== null && <div className="chart-tip" style={{ left: clamp(x(active), Math.min(119, width / 2), Math.max(width - 119, width / 2)) }} role="status" aria-live="polite">
          <strong>{dayLabel.format(utcDay(days[active].date))}</strong>
          {/* Top of the stack first, so the list reads in the same order as the bar. */}
          <dl>{[...days[active].segments].reverse().map((segment) => <div key={segment.id}><dt className="chart-key" style={{ '--key': color(segment.id) } as CSSProperties}>{segment.name}</dt><dd>{number(segment.volume)}</dd></div>)}
            <div><dt>Total m³</dt><dd>{number(days[active].total)}</dd></div></dl>
        </div>}
      </div>
      {unknownVolumeCount > 0 && <figcaption>Sin dibujar por volumen desconocido: {number(unknownVolumeCount)}.</figcaption>}
    </figure>
  </Section>;
}
